import React, { useState, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { EPSProvider, CreditCard as EPSCreditCard } from '@telus/eps-components';
import { Spacer, Button, Notification, IconButton, Typography } from '@telus-uds/components-web';

import { useTheme } from '../../BrandThemeProvider';
import { linkFormContext } from '../../contexts/linkFormContext';
import { Disclaimers } from '../disclaimers/Disclaimers';
import { Signature } from '../signature/Signature';
import ProcessingOverlay from '../processing-overlay/ProcessingOverlay';

export const TokenizedCardForm = () => {
  const {
    linkType,
    sessionToken,
    avsPostalChk,
    clientId,
    epsEnv,
    applicableDisclaimers,
    disclaimerConsents,
    setDisclaimerConsents,
    setCardToken,
    setSubmitting,
    setMethodCaptureError,
    signatureRequired,
    setSignature,
  } = useContext(linkFormContext);

  const { currentBrand } = useTheme();
  const { t, i18n } = useTranslation();
  const tokenBtnRef = useRef(null);
  const [processingTokenization, setProcessingTokenization] = useState(false);
  const [tokenizationError, setTokenizationError] = useState(null);
  const [signatureValid, setSignatureValid] = useState(!signatureRequired);

  const onTokenize = async () => {
    setProcessingTokenization(true);
    try {
      await tokenBtnRef.current?.onSubmit();
    } catch (error) {
      throw error;
    } finally {
      setProcessingTokenization(false);
    }
  };

  const handleTokenization = (response) => {
    if (response.isValid && response.id) {
      setCardToken(response.id);
      setSubmitting(true);
    } else {
      console.error('Invalid Tokenization');
      setTokenizationError(t('error.tokenization'));
    }
  };

  const handleEPSError = (err) => {
    const finalErrorCodes = ['40002', '40003']; // session token expired
    console.error('Tokenization Error:', err);
    if (finalErrorCodes.includes(err.code)) {
      setMethodCaptureError(t('error.tokenization'));
    }
    setTokenizationError(t('error.tokenization'));
  };

  const disableSubmit = () => {
    const disclaimersConsented = Object.values(disclaimerConsents).every((consent) => consent);
    return !disclaimersConsented || !signatureValid || processingTokenization;
  };

  const epsSdkAppName = `link-${clientId}`.replace(/[^a-zA-Z0-9\-_]/g, '').slice(0, 32);

  return (
    <>
      <ProcessingOverlay isProcessing={processingTokenization} type="tokenization" />
      <EPSProvider
        theme={currentBrand} // Assuming the brand value matches the theme name
        fetchEpsSessionToken={() => Promise.resolve(sessionToken)}
        lang={i18n.language}
        env={epsEnv}
        onError={handleEPSError}
        appName={epsSdkAppName}
      >
        {tokenizationError && (
          <>
            <Notification variant={{ validation: 'error' }}>
              <Typography>{tokenizationError}</Typography>
              <IconButton action="close" onPress={() => setTokenizationError(null)} />
            </Notification>
            <Spacer space={4} />
          </>
        )}
        <EPSCreditCard
          onTokenized={handleTokenization}
          onError={handleEPSError}
          collectPostalCode={avsPostalChk}
          hideSubmitButton={true}
          ref={tokenBtnRef}
        />
        <Disclaimers
          disclaimers={applicableDisclaimers}
          consents={disclaimerConsents}
          setConsents={setDisclaimerConsents}
        />
        {signatureRequired && <Signature setSignature={setSignature} setSignatureValid={setSignatureValid} />}
        <Spacer space={1} />
        <Button
          ref={tokenBtnRef}
          onPress={() => onTokenize()}
          disabled={disableSubmit()}
          variant={{ priority: 'high' }}
        >
          {t(`tokenBtn.${linkType}`)}
        </Button>
      </EPSProvider>
    </>
  );
};
